// ================= BASE =================
// Prefer env override, fallback to production API.
// CRA exposes env vars prefixed with REACT_APP_.
const BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "https://api.growthstationco.com";

// ================= ERROR HELPER =================
// Custom error class to preserve response data structure
class ApiError extends Error {
  constructor(response, data) {
    super(data?.message || "API request failed");
    this.response = response;
    this.data = data;
  }
}

// ================= HELPERS =================
async function parseResponseBody(response) {
  const text = await response.text();
  if (!text) return null;
  try { return JSON.parse(text); }
  catch { return text; }
}

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  if (!token) return { "Content-Type": "application/json", Accept: "application/json" };
  return {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: token, // ✅ بدون Bearer
  };
};

const getAuthHeadersFormData = () => {
  const token = localStorage.getItem("token");
  return {
    Accept: "application/json",
    // ✅ أضفنا Bearer
    ...(token ? { Authorization: `${token}` } : {}),
    // ✅ Content-Type متضافش خالص — المتصفح بيحدده مع الـ boundary أوتوماتيك
  };
};

// ================= AUTH =================
export const loginApi = async (data) => {
  const res = await fetch(`${BASE_URL}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  if (json?.accessToken) localStorage.setItem("token", json.accessToken);
  return json;
};

export const getUsers = async () => {
  const res = await fetch(`${BASE_URL}/employee/`, { headers: getAuthHeaders() });
  const json = await parseResponseBody(res);
  if (res.status === 404) {
    return { data: [] };
  }
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

export const createUser = async (data) => {
  const res = await fetch(`${BASE_URL}/auth/Register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

export const editUser = async (id, data) => {
  const res = await fetch(`${BASE_URL}/auth/updateUser/${id}`, {
    method: "PUT", headers: getAuthHeaders(), body: JSON.stringify(data),
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

export const deleteUser = async (id) => {
  const res = await fetch(`${BASE_URL}/auth/${id}`, { method: "DELETE", headers: getAuthHeaders() });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

// ================= SESSION =================
let heartbeatDisabled = false;
let heartbeatInterval = null;

export const startSession = async (data) => {
  const res = await fetch(`${BASE_URL}/sessions/start`, {
    method: "POST", headers: getAuthHeaders(), body: JSON.stringify(data),
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

export const endSession = async (data) => {
  try {
    const res = await fetch(`${BASE_URL}/sessions/end`, {
      method: "POST", headers: getAuthHeaders(), body: JSON.stringify(data),
    });
    if (res.status === 404) return { skipped: true };
    const json = await parseResponseBody(res);
    if (!res.ok) throw new ApiError(res, json);
    return json;
  } catch { return { skipped: true }; }
};

export const updateSessionHeartbeat = async (data) => {
  if (heartbeatDisabled) return;
  try {
    const res = await fetch(`${BASE_URL}/sessions/heartbeat`, {
      method: "POST", headers: getAuthHeaders(), body: JSON.stringify(data),
    });
    if (res.status === 404) { heartbeatDisabled = true; return; }
    return await parseResponseBody(res);
  } catch { return null; }
};

export const startHeartbeat = (data) => {
  if (heartbeatInterval || heartbeatDisabled) return;
  heartbeatInterval = setInterval(() => updateSessionHeartbeat(data), 15000);
};

export const stopHeartbeat = () => {
  clearInterval(heartbeatInterval);
  heartbeatInterval = null;
};

// ================= EXTRA USER FEATURES =================
export const updateProfilePicture = async (userId, file) => {
  const formData = new FormData();
  formData.append("file", file);
  const res = await fetch(`${BASE_URL}/auth/profilePicture/${userId}`, {
    method: "POST",
    headers: getAuthHeadersFormData(),
    body: formData,
  });
  const data = await parseResponseBody(res);

  // ✅ أضف السطرين دول
  console.log("STATUS:", res.status);
  console.log("BODY:", JSON.stringify(data));

  if (!res.ok) throw new ApiError(res, data);
  return data;
};

export const updatePassword = async (oldPassword, newPassword) => {
  const res = await fetch(`${BASE_URL}/auth/update-password`, {
    method: "PUT", headers: getAuthHeaders(), body: JSON.stringify({ oldPassword, newPassword }),
  });
  const data = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, data);
  return data;
};

export const getEmployeeProfile = async (userId) => {
  const res = await fetch(`${BASE_URL}/employee/profile/${userId}`, {
    method: "GET", headers: getAuthHeaders(),
  });
  const data = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, data);
  return data;
};

export const getDeletedUsers = async () => {
  const res = await fetch(`${BASE_URL}/auth/deletedUsers`, { method: "GET", headers: getAuthHeaders() });
  const data = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, data);
  return data;
};

export const restoreUser = async (id) => {
  const res = await fetch(`${BASE_URL}/auth/retrieveUser/${id}`, { method: "PATCH", headers: getAuthHeaders() });
  const data = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, data);
  return data;
};

// ================= VERIFY USERS =================
export const getUnverifiedUsers = async () => {
  const res = await fetch(`${BASE_URL}/auth/unverifiedUsers`, { method: "GET", headers: getAuthHeaders() });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new Error(json?.message || "Error loading unverified users");
  return json;
};

export const verifyUser = async (id) => {
  const res = await fetch(`${BASE_URL}/auth/verifyUser/${id}`, { method: "PUT", headers: getAuthHeaders() });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new Error(json?.message || "Error verifying user");
  return json;
};

// ================= CLIENTS =================
export const getClients = async () => {
  const res = await fetch(`${BASE_URL}/client/`, { headers: getAuthHeaders() });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

export const getClient = async (clientId) => {
  const res = await fetch(`${BASE_URL}/client/${clientId}`, { headers: getAuthHeaders() });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

export const createClient = async (data) => {
  const formData = new FormData();
  if (data.name) formData.append("name", data.name);
  if (data.email) formData.append("email", data.email);
  if (data.phone) formData.append("phone", data.phone);
  if (data.company) formData.append("company", data.company);
  if (data.contactInfo?.length) {
    data.contactInfo.forEach((item, i) => {
      formData.append(`contactInfo[${i}][type]`, item.type);
      formData.append(`contactInfo[${i}][value]`, item.value);
    });
  }
  if (data.notes) formData.append("notes", data.notes);
  if (data.agreedPrice !== undefined && data.agreedPrice !== "") formData.append("agreedPrice", Number(data.agreedPrice));
  if (data.earned !== undefined && data.earned !== "") formData.append("earned", Number(data.earned));
  if (data.contract) formData.append("contract", data.contract);
  if (data.projects?.length) {
    data.projects.forEach((projectId, i) => {
      formData.append(`projects[${i}]`, projectId);
    });
  }
  if (data.tasks?.length) {
    data.tasks.forEach((taskId, i) => {
      formData.append(`tasks[${i}]`, taskId);
    });
  }
  if (data.transactions?.length) {
    data.transactions.forEach((amount, i) => {
      formData.append(`transactions[${i}]`, Number(amount));
    });
  }
  if (data.attachments?.length) {
    data.attachments.forEach((url, i) => {
      formData.append(`attachments[${i}]`, url);
    });
  }
  const res = await fetch(`${BASE_URL}/client/create`, {
    method: "POST",
    headers: getAuthHeadersFormData(),
    body: formData,
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

export const updateClient = async (clientId, data, contractFile = null, attachmentFiles = null) => {
  const formData = new FormData();
  if (data.name !== undefined) formData.append("name", data.name);
  if (data.email !== undefined) formData.append("email", data.email);
  if (data.phone !== undefined) formData.append("phone", data.phone);
  if (data.company !== undefined) formData.append("company", data.company);
  if (data.status !== undefined) formData.append("status", data.status);
  if (data.notes !== undefined) formData.append("notes", data.notes);
  if (data.agreedPrice !== undefined && data.agreedPrice !== "" && !isNaN(Number(data.agreedPrice))) {
    formData.append("agreedPrice", Number(data.agreedPrice));
  }
  if (data.earned !== undefined && data.earned !== "" && !isNaN(Number(data.earned))) {
    formData.append("earned", Number(data.earned));
  }
  if (data.restToEarn !== undefined && data.restToEarn !== "" && !isNaN(Number(data.restToEarn))) {
    formData.append("restToEarn", Number(data.restToEarn));
  }
  if (data.contactInfo?.length) {
    data.contactInfo.forEach((item, i) => {
      formData.append(`contactInfo[${i}][type]`, item.type);
      formData.append(`contactInfo[${i}][value]`, item.value);
    });
  }
  if (contractFile) formData.append("contract", contractFile);
  if (attachmentFiles?.length) {
    attachmentFiles.forEach((file) => formData.append("attachments", file));
  }
  const res = await fetch(`${BASE_URL}/client/${clientId}`, {
    method: "PATCH",
    headers: getAuthHeadersFormData(),
    body: formData,
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

export const deleteClient = async (clientId) => {
  const res = await fetch(`${BASE_URL}/client/${clientId}`, { method: "DELETE", headers: getAuthHeaders() });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

export const getDeletedClients = async () => {
  const res = await fetch(`${BASE_URL}/client/deleted`, { headers: getAuthHeaders() });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

export const restoreClient = async (clientId) => {
  const res = await fetch(`${BASE_URL}/client/restore/${clientId}`, { method: "PATCH", headers: getAuthHeaders() });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

// ================= TEAM OVERVIEW REPORTS =================
export const getMonthlyReport = async (teamId, year = null, month = null) => {
  let url = `${BASE_URL}/team-overview/${teamId}/monthly-report`;
  const params = [];
  if (year) params.push(`year=${year}`);
  if (month) params.push(`month=${month}`);
  if (params.length) url += `?${params.join("&")}`;
  const res = await fetch(url, { headers: getAuthHeaders() });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new Error(json?.message || "Error fetching monthly report");
  return json;
};

export const getAttendanceReport = async (teamId, startDate = null, endDate = null) => {
  let url = `${BASE_URL}/team-overview/${teamId}/attendance`;
  const params = [];
  if (startDate) params.push(`startDate=${startDate}`);
  if (endDate) params.push(`endDate=${endDate}`);
  if (params.length) url += `?${params.join("&")}`;
  const res = await fetch(url, { headers: getAuthHeaders() });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new Error(json?.message || "Error fetching attendance report");
  return json;
};

// ================= PROJECTS =================
const buildQuery = (filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
  const q = params.toString();
  return q ? `?${q}` : "";
};

export const getProjects = async (filters = {}) => {
  const res = await fetch(`${BASE_URL}/projects/${buildQuery(filters)}`, { headers: getAuthHeaders() });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new Error(json?.message || "Error loading projects");
  return json;
};

export const getProjectById = async (id) => {
  const res = await fetch(`${BASE_URL}/projects/${id}`, { headers: getAuthHeaders() });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new Error(json?.message || "Error loading project");
  return json;
};

export const createProjectApi = async (data) => {
  const fd = new FormData();
  if (data.title) fd.append("title", data.title);
  if (data.description) fd.append("description", data.description);
  if (data.status) fd.append("status", data.status);
  if (data.priority) fd.append("priority", data.priority);
  if (data.dueDate) fd.append("dueDate", data.dueDate);
  if (data.startDate) fd.append("startDate", data.startDate);
  if (Array.isArray(data.assignedTo)) {
    data.assignedTo.forEach((id, i) => fd.append(`assignedTo[${i}]`, id));
  }
  if (Array.isArray(data.tasks)) {
    data.tasks.forEach((id, i) => fd.append(`tasks[${i}]`, id));
  }
  if (Array.isArray(data.photos)) {
    data.photos.forEach((file) => fd.append("photos", file));
  }
  if (Array.isArray(data.attachments)) {
    data.attachments.forEach((file) => fd.append("attachments", file));
  }
  const token = localStorage.getItem("token");
  const res = await fetch(`${BASE_URL}/projects/add`, {
    method: "POST",
    headers: { Authorization: token }, // ✅ بدون Bearer للـ FormData
    body: fd,
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new Error(json?.message || "Error creating project");
  return json;
};

export const updateProjectApi = async (id, data) => {
  const res = await fetch(`${BASE_URL}/projects/${id}`, {
    method: "PATCH", headers: getAuthHeaders(), body: JSON.stringify(data),
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new Error(json?.message || "Error updating project");
  return json;
};

export const deleteProjectApi = async (id) => {
  const res = await fetch(`${BASE_URL}/projects/${id}`, {
    method: "DELETE", headers: getAuthHeaders(),
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new Error(json?.message || "Error deleting project");
  return json;
};

export const deleteProjectFileApi = async (projectId, fileId, type = "attachment") => {
  const res = await fetch(
    `${BASE_URL}/projects/${projectId}/file?fileId=${fileId}&type=${type}`,
    { method: "DELETE", headers: getAuthHeaders() }
  );
  const json = await parseResponseBody(res);
  if (!res.ok) throw new Error(json?.message || "Error deleting file");
  return json;
};

export const getDeletedProjectsApi = async () => {
  const res = await fetch(`${BASE_URL}/projects/deleted`, { headers: getAuthHeaders() });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new Error(json?.message || "Error loading deleted projects");
  return json;
};

export const restoreProjectApi = async (id) => {
  const res = await fetch(`${BASE_URL}/projects/restore/${id}`, {
    method: "PATCH", headers: getAuthHeaders(),
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new Error(json?.message || "Error restoring project");
  return json;
};

// ================= PAYROLL (updated to match the guide) =================
// Schema per employee PER MONTH+YEAR, with `absences` and `manualDeduction`.
// netSalary/dailyRate/absenceDeduction are computed server-side, never sent
// by the frontend.

// POST /payroll/create — application/json
// data: { userId, month (1-12), year, basicSalary, absences, bonus, manualDeduction, notes }
export const createPayroll = async (data) => {
  const res = await fetch(`${BASE_URL}/payroll/create`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

// GET /payroll?page=&limit=
export const getAllPayrolls = async (page = 1, limit = 10) => {
  const res = await fetch(`${BASE_URL}/payroll?page=${page}&limit=${limit}`, {
    headers: getAuthHeaders(),
  });
  const json = await parseResponseBody(res);
  if (res.status === 404) return { data: [] };
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

// GET /payroll?month=&year=&page=&limit=
// Used to feed the Monthly Report screen (Step 1) and the History view.
export const getPayrollsByMonth = async (month, year, page = 1, limit = 50) => {
  const res = await fetch(
    `${BASE_URL}/payroll?month=${month}&year=${year}&page=${page}&limit=${limit}`,
    { headers: getAuthHeaders() }
  );
  const json = await parseResponseBody(res);
  if (res.status === 404) return { data: [] };
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

// GET /payroll/:userId — also used to pre-fill basicSalary for a returning employee
export const getPayrollByUserId = async (userId) => {
  const res = await fetch(`${BASE_URL}/payroll/${userId}`, { headers: getAuthHeaders() });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

// PATCH /payroll/:payrollId — same field shape as create, all optional
export const updatePayroll = async (payrollId, data) => {
  const res = await fetch(`${BASE_URL}/payroll/${payrollId}`, {
    method: "PATCH",
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

export const deletePayroll = async (payrollId) => {
  const res = await fetch(`${BASE_URL}/payroll/${payrollId}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

export const restorePayroll = async (payrollId) => {
  const res = await fetch(`${BASE_URL}/payroll/${payrollId}`, {
    method: "PUT",
    headers: getAuthHeaders(),
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

export const getDeletedPayrolls = async () => {
  const res = await fetch(`${BASE_URL}/payroll/deleted`, { headers: getAuthHeaders() });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

// ================= EXPENSE (new module) =================

const buildExpenseFormData = (data, invoicePhotosFile) => {
  const fd = new FormData();
  if (data.date !== undefined) fd.append("date", data.date);
  if (data.category !== undefined) fd.append("category", data.category);
  if (data.description !== undefined) fd.append("description", data.description);
  if (data.amount !== undefined && data.amount !== "") fd.append("amount", Number(data.amount));
  if (data.paidBy !== undefined) fd.append("paidBy", data.paidBy);
  if (data.paymentMethod !== undefined) fd.append("paymentMethod", data.paymentMethod);
  if (data.notes !== undefined && data.notes !== null) fd.append("notes", data.notes);

  // ✅ جديد: لو المستخدم مسح الصورة الحالية من غير ما يرفع صورة جديدة
  if (data.removeInvoice) {
    fd.append("removeInvoice", "true");
  }

  // Only attach invoicePhoto if a new file was picked — omitting it on PATCH
  // preserves the existing photo (server behavior), unless removeInvoice is set.
  if (invoicePhotosFile) fd.append("invoicePhotos", invoicePhotosFile);

  return fd;
};

// POST /expense — multipart/form-data
export const createExpense = async (data, invoicePhotosFile = null) => {
  const res = await fetch(`${BASE_URL}/expense`, {
    method: "POST",
    headers: getAuthHeadersFormData(),
    body: buildExpenseFormData(data, invoicePhotosFile),
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

// GET /expense?page=&limit=
export const getExpenses = async (page = 1, limit = 10) => {
  const res = await fetch(`${BASE_URL}/expense?page=${page}&limit=${limit}`, {
    headers: getAuthHeaders(),
  });
  const json = await parseResponseBody(res);
  if (res.status === 404) return { data: [] };
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

export const getExpenseById = async (id) => {
  const res = await fetch(`${BASE_URL}/expense/${id}`, { headers: getAuthHeaders() });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

// GET /expense/by-category/:category
export const getExpensesByCategory = async (category) => {
  const res = await fetch(`${BASE_URL}/expense/by-category/${encodeURIComponent(category)}`, {
    headers: getAuthHeaders(),
  });
  const json = await parseResponseBody(res);
  if (res.status === 404) return { data: [] };
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

// GET /expense/by-date?from=&to=&page=&limit=
export const getExpensesByDateRange = async (from, to, page = 1, limit = 10) => {
  const res = await fetch(
    `${BASE_URL}/expense/by-date?from=${from}&to=${to}&page=${page}&limit=${limit}`,
    { headers: getAuthHeaders() }
  );
  const json = await parseResponseBody(res);
  if (res.status === 404) return { data: [] };
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

// PATCH /expense/:id — multipart/form-data, all fields optional
export const updateExpense = async (id, data, invoicePhotosFile = null) => {
  const res = await fetch(`${BASE_URL}/expense/${id}`, {
    method: "PATCH",
    headers: getAuthHeadersFormData(),
    body: buildExpenseFormData(data, invoicePhotosFile),
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

// DELETE /expense/:id — soft delete
export const deleteExpense = async (id) => {
  const res = await fetch(`${BASE_URL}/expense/${id}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

// ================= MONTHLY REPORT (new module) =================
// Note: this is the standalone Monthly Report resource (month/year based).
// It is distinct from getMonthlyReport() above, which fetches a team's
// monthly report under /team-overview/:teamId/monthly-report — both are
// kept since they hit different endpoints.

// POST /monthly-report — application/json
// data: { month, year, attendance?, risksAndDecisions?, recommendations?, ...overrides }
export const createMonthlyReport = async (data) => {
  const res = await fetch(`${BASE_URL}/monthly-report`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

export const getMonthlyReports = async () => {
  const res = await fetch(`${BASE_URL}/monthly-report`, { headers: getAuthHeaders() });
  const json = await parseResponseBody(res);
  if (res.status === 404) return { data: [] };
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

// GET /monthly-report/by-month?month=&year=
export const getMonthlyReportByMonth = async (month, year) => {
  const res = await fetch(`${BASE_URL}/monthly-report/by-month?month=${month}&year=${year}`, {
    headers: getAuthHeaders(),
  });
  const json = await parseResponseBody(res);
  if (res.status === 404) return { data: null };
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

export const getMonthlyReportById = async (id) => {
  const res = await fetch(`${BASE_URL}/monthly-report/${id}`, { headers: getAuthHeaders() });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

// DELETE /monthly-report/:id — hard delete
export const deleteMonthlyReport = async (id) => {
  const res = await fetch(`${BASE_URL}/monthly-report/${id}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

// PUT /monthly-report/:id — update existing report
export const updateMonthlyReport = async (id, data) => {
  const res = await fetch(`${BASE_URL}/monthly-report/${id}`, {
    method: "PUT",
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

// ================= Team Overview  =================
export const logActivity = async (status) => {
  const res = await fetch(`${BASE_URL}/log`, {
    method: "POST", headers: getAuthHeaders(), body: JSON.stringify({ status }),
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

export const getLatestActivity = async () => {
  const res = await fetch(`${BASE_URL}/log/latest`, { headers: getAuthHeaders() });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

function formatLocalYYYYMMDD(d) {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function normalizeLogStatus(statusRaw) {
  const s = String(statusRaw ?? "").toLowerCase().trim();
  if (!s) return "offline";
  if (s === "day off" || s === "day-off" || s === "day_off") return "dayoff";
  if (s.includes("break")) return "break";
  if (s === "off" || s === "offline") return "offline";
  return s;
}

export const getAllUsersActivityByDay = async (date = null) => {
  try {
    const selectedDate = date || formatLocalYYYYMMDD(new Date());

    const usersRes = await fetch(`${BASE_URL}/employee/`, { headers: getAuthHeaders() });
    const usersJson = await parseResponseBody(usersRes);
    if (!usersRes.ok) throw new ApiError(usersRes, usersJson);

    const users = Array.isArray(usersJson)
      ? usersJson
      : (usersJson?.data ?? usersJson?.users ?? []);

    if (!users.length) return { data: [] };

    const results = await Promise.allSettled(
      users.map(async (user) => {
        const uid = user._id ?? user.id;
        if (!uid) return null;

        const userBase = {
          _id: uid,
          userName: user.userName ?? user.name ?? "Unknown",
          avatar: user.avatar ?? null,
          department: user.department ?? "—",
          position: user.position ?? "—",
          role: user.role ?? "—",
        };

        const res = await fetch(
          `${BASE_URL}/log/by-day?date=${selectedDate}&userId=${uid}`,
          { headers: getAuthHeaders() }
        );
        const data = await parseResponseBody(res);

        if (!res.ok || !data) {
          return [{
            status: "offline",
            startTime: null,
            endTime: null,
            _day: selectedDate,
            userId: userBase,
          }];
        }

        const logs = Array.isArray(data) ? data : (data?.data ?? []);

        if (!logs.length) {
          return [{
            status: "offline",
            startTime: null,
            endTime: null,
            _day: selectedDate,
            userId: userBase,
          }];
        }

        return logs.map((log) => ({
          ...log,
          status: normalizeLogStatus(log.status),
          _day: selectedDate,
          userId: userBase,
        }));
      })
    );

    const allLogs = results
      .filter((r) => r.status === "fulfilled" && r.value)
      .flatMap((r) => r.value);

    return { data: allLogs };
  } catch (err) {
    console.error("❌ getAllUsersActivityByDay error:", err);
    throw err;
  }
};

export const getUserActivityByDay = async (userId) => {
  const res = await fetch(`${BASE_URL}/log/user/${userId}`, { headers: getAuthHeaders() });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

// ================= CONTACT =================
export const submitContact = async (formData) => {
  const res = await fetch(`${BASE_URL}/contact/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(formData),
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

export const getAllContacts = async (params = {}) => {
  const query = new URLSearchParams();
  if (params.status) query.append("status", params.status);
  if (params.page) query.append("page", params.page);
  const qs = query.toString() ? `?${query.toString()}` : "";
  const res = await fetch(`${BASE_URL}/contact/${qs}`, { headers: getAuthHeaders() });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

export const getContactById = async (contactId) => {
  const res = await fetch(`${BASE_URL}/contact/${contactId}`, { headers: getAuthHeaders() });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

export const updateContactStatus = async (contactId, status) => {
  const res = await fetch(`${BASE_URL}/contact/${contactId}`, {
    method: "PATCH",
    headers: getAuthHeaders(),
    body: JSON.stringify({ status }),
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

export const deleteContact = async (contactId) => {
  const res = await fetch(`${BASE_URL}/contact/${contactId}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

// ==================== Reports ====================

export const getAllReports = async () => {
  const res = await fetch(`${BASE_URL}/report`, { headers: getAuthHeaders() });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

export const getDeletedReports = async () => {
  const res = await fetch(`${BASE_URL}/report/deleted`, { headers: getAuthHeaders() });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

export const getReportById = async (id) => {
  const res = await fetch(`${BASE_URL}/report/${id}`, { headers: getAuthHeaders() });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

export const createReport = async (formData) => {
  const res = await fetch(`${BASE_URL}/report`, {
    method: "POST",
    headers: getAuthHeadersFormData(),
    body: formData,
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

export const deleteReport = async (id) => {
  const res = await fetch(`${BASE_URL}/report/${id}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

export const updateReport = async (id, data) => {
  const res = await fetch(`${BASE_URL}/report/${id}`, {
    method: "PATCH",
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

export const restoreReport = async (id) => {
  const res = await fetch(`${BASE_URL}/report/${id}`, {
    method: "PATCH",
    headers: getAuthHeaders(),
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};


// ================= CAREERS (Applications) =================

export const createApplication = async (data) => {
  const formData = new FormData();

  formData.append("fullName", data.fullName || data.name || "");
  formData.append("name", data.fullName || data.name || "");
  formData.append("email", data.email || "");
  formData.append("phone", data.phone || "");
  formData.append("department", data.department || "");
  formData.append("position", data.position || data.jobTitle || data.title || "");
  formData.append("jobTitle", data.position || data.jobTitle || data.title || "");
  formData.append("portfolio", data.portfolioUrl || data.portfolio || "");
  formData.append("message", data.message || "");
  if (data.jobId) formData.append("jobId", data.jobId);
  if (data.announcementId) formData.append("announcementId", data.announcementId);

  if (data.resumeFile) {
    formData.append("resume", data.resumeFile);
  }

  const res = await fetch(`${BASE_URL}/application/`, {
    method: "POST",
    headers: {
      Accept: "application/json",
    },
    body: formData,
  });

  const json = await parseResponseBody(res);

  if (!res.ok) throw new ApiError(res, json);

  return json;
};

export const getApplications = async ({ page = 1, limit = 10 } = {}) => {
  const res = await fetch(
    `${BASE_URL}/application/?page=${page}&limit=${limit}`,
    { headers: getAuthHeaders() }
  );
  const json = await parseResponseBody(res);
  if (res.status === 404) return { data: [] };
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

export const getApplicationById = async (id) => {
  const res = await fetch(`${BASE_URL}/application/${id}`, {
    headers: getAuthHeaders(),
  });

  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

export const deleteApplication = async (id) => {
  const res = await fetch(`${BASE_URL}/application/${id}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });

  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

// ================= ANNOUNCEMENTS =================
export const createAnnouncement = async (data) => {
  const res = await fetch(`${BASE_URL}/announcement`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

export const getAllAnnouncements = async () => {
  const res = await fetch(`${BASE_URL}/announcement`, {
    headers: getAuthHeaders(),
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

export const getAnnouncementById = async (id) => {
  const res = await fetch(`${BASE_URL}/announcement/${id}`, {
    headers: getAuthHeaders(),
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

export const updateAnnouncement = async (id, data) => {
  const res = await fetch(`${BASE_URL}/announcement/${id}`, {
    method: "PATCH",
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

export const deleteAnnouncement = async (id) => {
  const res = await fetch(`${BASE_URL}/announcement/${id}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

// ================= BRIEF =================
export const getAllBriefs = async (page = 1, limit = 10) => {
  const res = await fetch(`${BASE_URL}/brief?page=${page}&limit=${limit}`, {
    headers: getAuthHeaders(),
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

export const getBriefById = async (id) => {
  const res = await fetch(`${BASE_URL}/brief/${id}`, {
    headers: getAuthHeaders(),
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

export const createBrief = async (data) => {
  const res = await fetch(`${BASE_URL}/brief`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

export const updateBrief = async (id, data) => {
  const res = await fetch(`${BASE_URL}/brief/${id}`, {
    method: "PATCH",
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

export const deleteBrief = async (id) => {
  const res = await fetch(`${BASE_URL}/brief/${id}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

// ================= DAILY REPORTS =================
export const getAllDailyReports = async (page = 1, limit = 10) => {
  const url = `${BASE_URL}/daily-report?page=${page}&limit=${limit}`;

  const res = await fetch(url, {
    headers: getAuthHeaders(),
  });

  const json = await parseResponseBody(res);

  if (!res.ok) throw new ApiError(res, json);

  return json;
};

export const getDailyReportsByDate = async (date, page = 1, limit = 10) => {
  const url = `${BASE_URL}/daily-report/by-date/${date}?page=${page}&limit=${limit}`;

  const res = await fetch(url, {
    headers: getAuthHeaders(),
  });

  const json = await parseResponseBody(res);

  if (!res.ok) throw new ApiError(res, json);

  return json;
};

export const getDailyReportById = async (id) => {
  const url = `${BASE_URL}/daily-report/${id}`;

  const res = await fetch(url, {
    headers: getAuthHeaders(),
  });

  const json = await parseResponseBody(res);

  if (!res.ok) throw new ApiError(res, json);

  return json;
};

export const createDailyReport = async (data) => {
  const url = `${BASE_URL}/daily-report`;

  const res = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  const json = await parseResponseBody(res);

  if (!res.ok) throw new ApiError(res, json);

  return json;
};

export const deleteDailyReport = async (id) => {
  const url = `${BASE_URL}/daily-report/${id}`;

  console.log("DELETE REPORT URL:", url);

  const res = await fetch(url, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });

  const json = await parseResponseBody(res);

  console.log("DELETE REPORT RESPONSE:", json);

  if (!res.ok) throw new ApiError(res, json);

  return json;
};

// ================= CHAT MESSAGES =================
export const sendMessage = async (roomId, message, type = "text") => {
  const res = await fetch(`${BASE_URL}/chat/${roomId}/message`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ message, type }),
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

// ✅ NEW: بيرجع كل الـ rooms (private + group) اللي اليوزر الحالي
// عضو فيها فعليًا من الداتابيز. دي نفس النقطة اللي Messages.jsx
// بيستخدمها، لكن دلوقتي بنستخدمها كمان من AuthContext عشان نعمل
// join حقيقي بالـ roomId الصح لكل الغرف بتاعتك من أول ما تفتح

// ================= DAY OFF MANAGEMENT =================
export const addDayOff = async (userId, dayOffData) => {
  const res = await fetch(`${BASE_URL}/auth/addDayOff/${userId}`, {
    method: "PATCH",
    headers: getAuthHeaders(),
    body: JSON.stringify(dayOffData),
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

export const deleteDayOff = async (userId, dayOffId) => {
  const res = await fetch(`${BASE_URL}/auth/deleteDayOff/${userId}/${dayOffId}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

// ================= NOTES MANAGEMENT =================
export const addNote = async (userId, noteData) => {
  const res = await fetch(`${BASE_URL}/employee/note/${userId}`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(noteData),
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

export const updateNote = async (userId, noteData) => {
  const res = await fetch(`${BASE_URL}/employee/note/${userId}`, {
    method: "PATCH",
    headers: getAuthHeaders(),
    body: JSON.stringify(noteData),
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};

export const deleteNote = async (userId, noteData) => {
  const res = await fetch(`${BASE_URL}/employee/note/${userId}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
    body: JSON.stringify(noteData),
  });
  const json = await parseResponseBody(res);
  if (!res.ok) throw new ApiError(res, json);
  return json;
};
// التطبيق — مش بس لما تدخل صفحة الرسايل — عشان الإشعارات توصلك
// وانت في أي صفحة تانية.
export const getChatRooms = async () => {
  const res = await fetch(`${BASE_URL}/chat/rooms`, { headers: getAuthHeaders() });
  const json = await parseResponseBody(res);
  if (res.status === 404) return { data: [] };
  if (!res.ok) throw new ApiError(res, json);
  return json;
};